import { rewriteSearchPhrases } from "./search.js";
// ---------------------------------------------------------------------------
// Question to plan
// ---------------------------------------------------------------------------
const STOP = new Set(`a an the and or of for to in at on with my me i we you your our is are am was were be do does did can could should would
   will who whom whose which what where when how there here any anyone anybody someone somebody people person contacts contact
   friends friend colleagues colleague know knows known about else all some that this these those it its them they he she him her
   his hers please just really also still again now then please`
    .split(/\s+/)
    .filter(Boolean));
/** Crude singularisation, the matching package's rule, so "taxes" finds "tax" and "designers" finds "designer". */
export function singular(token) {
    if (token.length <= 3)
        return token;
    if (token.endsWith("ies"))
        return token.slice(0, -3) + "y";
    if (token.endsWith("sses"))
        return token.slice(0, -2);
    if (/(xes|ches|shes)$/.test(token))
        return token.slice(0, -2);
    if (token.endsWith("ss") || token.endsWith("us"))
        return token;
    if (token.endsWith("s"))
        return token.slice(0, -1);
    return token;
}
/** Lower-case content words of a phrase, singularised, in order, without duplicates. */
export function keyTokens(text) {
    const out = [];
    for (const raw of text.toLowerCase().replace(/[^\p{L}\p{N}\s]+/gu, " ").split(/\s+/)) {
        if (raw.length < 2 || STOP.has(raw))
            continue;
        const t = singular(raw);
        if (!out.includes(t))
            out.push(t);
    }
    return out;
}
function quote(value) {
    return /\s/.test(value) ? `"${value}"` : value;
}
function keywordQuery(keyword, value) {
    return `${keyword}:${quote(value.toLowerCase())}`;
}
/** The exact query, then (for skills and titles) the same field over any of the subject's stemmed words, then free text. */
function fieldCandidates(keyword, subject, opts) {
    const out = [{ query: keywordQuery(keyword, subject), scope: "exact" }];
    const tokens = keyTokens(subject);
    if (opts.tokens && (tokens.length > 1 || (tokens.length === 1 && tokens[0] !== subject.toLowerCase()))) {
        const alt = tokens.length === 1 ? keywordQuery(keyword, tokens[0]) : `[${tokens.map((t) => keywordQuery(keyword, t)).join(" ")}]`;
        out.push({ query: alt, scope: "exact" });
    }
    if (opts.freeText)
        out.push({ query: tokens.length ? tokens.join(" ") : subject.toLowerCase(), scope: "broad" });
    return out;
}
function clean(value) {
    return value
        .trim()
        .replace(/^[\s"':,;-]+|[\s"'?.!,;:-]+$/g, "")
        .replace(/^(?:the|a|an|my|some|our)\s+/i, "")
        .trim();
}
/** Cuts a captured subject at the first clause that belongs to another rule and hands that tail back. */
function splitTail(subject, also) {
    const m = /^(.*?)\s+((?:who|that|which)\s+.*|(?:and|or)\s+.*|can\s+.*|could\s+.*|with\s+.*|for\s+.*|about\s+.*)$/i.exec(subject) ?? (also ? also.exec(subject) : null);
    if (!m)
        return { head: clean(subject), tail: "" };
    return { head: clean(m[1]), tail: m[2].trim() };
}
const WINDOWS = [
    { re: /\btoday\b/, token: "today", word: "today", days: 1 },
    { re: /\btomorrow\b/, token: "tomorrow", word: "tomorrow", days: 2 },
    { re: /\bthis\s+week\b/, token: "thisweek", word: "this week", days: 7 },
    { re: /\bnext\s+week\b/, token: "nextweek", word: "next week", days: 14 },
    { re: /\bthis\s+month\b/, token: "thismonth", word: "this month", days: 31 },
    { re: /\bnext\s+month\b/, token: "nextmonth", word: "next month", days: 62 },
];
const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
const PRONOUNS = new Set(["them", "him", "her", "this person", "that person", "someone", "anyone", "people", "me"]);
/** The name a question is about, after the question's own words are removed; null when it names nobody. */
function nameIn(text, extraStop) {
    const stop = new Set(extraStop);
    const words = clean(text)
        .split(/\s+/)
        .filter((w) => w && !STOP.has(w.toLowerCase()) && !stop.has(w.toLowerCase()));
    const name = words.join(" ").trim();
    if (!name || PRONOUNS.has(name.toLowerCase()))
        return null;
    return name;
}
const LAST_TALK_WORDS = ["when", "did", "last", "time", "was", "spoke", "speak", "talk", "talked", "talking", "see", "saw", "seen", "met", "meet", "hear", "heard", "from", "email", "emailed", "call", "called", "message", "messaged", "text", "texted", "catch", "caught", "up", "touch", "contact", "contacted", "since", "long", "ago", "has", "have", "been", "chat", "chatted", "get", "together"];
const ABOUT_WORDS = ["tell", "say", "summarize", "summarise", "summary", "rundown", "background", "info", "information", "details", "story", "remind", "give", "show", "have", "got", "on", "up", "doing", "recap", "brief", "profile"];
const INTRO_WORDS = ["introduce", "intro", "introduction", "introductions", "connect", "help", "meet", "use", "needs", "need", "offers", "offer", "does", "provides", "good", "benefit", "from", "pair"];
/** Grammar from the words before the subject: "closest people at Google", "hub users in Denver". */
function prefixClauses(prefix) {
    const words = prefix
        .toLowerCase()
        .replace(/[?!.,;]/g, " ")
        .split(/\s+/)
        .filter((w) => w && !STOP.has(w) && !/^(?:else|work|works|working|live|lives|living|based|located|employed|anybody|everyone|everybody|list|show|find|are|is|there|do|does|did)$/.test(w));
    if (words.length === 0)
        return [];
    const { text } = rewriteSearchPhrases(words.join(" "));
    const tokens = text.trim().split(/\s+/).filter(Boolean);
    if (tokens.length && tokens.every((w) => /^[\[\]()-]*(?:[a-z_]+[:<>=!]\S|has:|missing:)/.test(w) || /^[\[\]()]+$/.test(w)))
        return [text.trim().replace(/\s+/g, " ")];
    return [];
}
function isGrammar(text) {
    const words = text.trim().split(/\s+/).filter(Boolean);
    return words.length > 0 && words.every((w) => /^[\[\]()-]*(?:[a-z_]+[:<>=!]\S|has:|missing:)/.test(w) || /^[\[\]()]+$/.test(w));
}
const GROUP_FILLER = /\b(?:who's|whos|whose|who|is|are|was|were|in|the|my|our|of|to|from|a|an|all|everyone|everybody|people|person|members?|member|belongs?|belonging|belong|list|show|me|display|open|put|did|i|do|does|group|groups|called|named|part|which|what|have|got|up|contacts?|friends?)\b/g;
/** Turns a question into the grammar queries that answer it. Pure: the same question always gives the same plan. */
export function planQuestion(question, ctx = {}) {
    const raw = question.trim().replace(/\s+/g, " ");
    const q = raw
        .replace(/[?!.]+$/g, "")
        .replace(/\s+in\s+(?:their|the|a)\s+(?:job\s+)?title$/i, "")
        .trim();
    const lower = q.toLowerCase();
    const plan = (intent, subject, candidates, window = null, days = 0, windowToken = null) => ({ intent, subject, candidates, window, windowToken, days });
    const personName = ctx.person ? ctx.person.displayName : null;
    if (!q)
        return plan("search", null, []);
    // Grammar typed by hand passes straight through.
    if (/(^|[\s(\[-])(?:[a-z_]+[:<>=!]\S|has:|missing:)/i.test(q) && !/\?$/.test(raw))
        return plan("search", null, [{ query: q, scope: "exact" }]);
    // When did we last talk (to X)?
    if (/\b(?:when|how long)\b.*\b(?:last|since)\b/.test(lower) || /\blast (?:talk|talked|spoke|time|contact|touch|met|saw|email|emailed|call|called|message|messaged|chat|chatted)\b/.test(lower)) {
        const name = nameIn(q, LAST_TALK_WORDS) ?? personName;
        return plan("last_talk", name, name ? [{ query: keywordQuery("name", name), scope: "exact" }] : []);
    }
    // Who could I introduce X to?
    const intro = /\b(?:introduce|intro)\s+(.+?)\s+to\b/.exec(lower) ??
        /\b(?:intro|introduction|introductions)\s+(?:for|to)\s+(?!(?:a|an|the|some)\b)(.+)$/.exec(lower) ??
        /\bconnect\s+(.+?)\s+(?:with|to)\b/.exec(lower) ??
        /\bwho\s+(?:could|can|should|would|might)\s+(.+?)\s+(?:help|meet|benefit)\b/.exec(lower) ??
        /\bwho\s+(?:needs?|could use|wants?)\s+what\s+(.+?)\s+(?:offers?|does|provides?)\b/.exec(lower) ??
        /\bwho\s+(?:could|should|would|can)\s+(?:i\s+)?(?:pair|match)\s+(.+?)\s+with\b/.exec(lower);
    if (intro || /\b(?:introductions?|intros?)\b/.test(lower)) {
        const captured = intro ? clean(intro[1]).replace(/^(?:i|we)\s+/, "") : "";
        const name = captured && !PRONOUNS.has(captured) && !/^(?:i|we|my|someone)$/.test(captured) ? nameIn(captured, INTRO_WORDS) : null;
        const subject = name ?? personName;
        if (subject || /\b(?:introduce|intro|introductions?)\b/.test(lower))
            return plan("introduce", subject, subject ? [{ query: keywordQuery("name", subject), scope: "exact" }] : []);
    }
    // Who am I meeting this week?
    const meetingWords = /\b(?:meeting|meetings|seeing|calendar|appointments?)\b/.test(lower) || /\bwho (?:do|will|am|should) i (?:see|seeing|meet|meeting)\b/.test(lower) || /\bwhat(?:'s| is| do i have) coming up\b/.test(lower);
    if (meetingWords && !/\b(?:haven'?t|have not|never|last)\b/.test(lower)) {
        if (/\b(?:who|what|which|upcoming|coming up|next|on my|do i have|have i got|this|today|tomorrow|any)\b/.test(lower)) {
            const w = WINDOWS.find((x) => x.re.test(lower));
            if (w)
                return plan("meetings", null, [{ query: `nextmeeting:${w.token}`, scope: "exact" }], w.word, w.days, w.token);
            return plan("meetings", null, [{ query: "nextmeeting>=today nextmeeting<=+7d", scope: "exact" }], "in the next 7 days", 7);
        }
    }
    // Birthdays
    if (/\bbirthdays?\b|\bborn\b/.test(lower)) {
        const w = WINDOWS.find((x) => x.re.test(lower));
        if (w)
            return plan("birthdays", null, [{ query: `birthday:${w.token}`, scope: "exact" }], w.word, w.days);
        const month = MONTHS.findIndex((m) => new RegExp(`\\b${m}\\b`).test(lower));
        if (month >= 0)
            return plan("birthdays", null, [{ query: `birthday:${month + 1}`, scope: "exact" }], `in ${MONTHS[month][0].toUpperCase()}${MONTHS[month].slice(1)}`, 366);
        return plan("birthdays", null, [{ query: "[birthday:thismonth birthday:nextmonth]", scope: "exact" }], "this month or next", 62);
    }
    // Who haven't I talked to in a while?
    if (/\b(?:haven'?t|have\s+not|havent|hasn'?t|didn'?t|not)\s+(?:i\s+)?(?:talked|spoken|seen|met|heard|caught\s+up|reached|connected|messaged|emailed|called|texted|been\s+in\s+touch)/.test(lower) ||
        /\b(?:lost\s+touch|out\s+of\s+touch|in\s+a\s+while|fallen\s+off|gone\s+quiet|stale|neglected|neglecting|reconnect|overdue|catch\s+up\s+with|owe\s+(?:a\s+)?(?:call|message|email))\b/.test(lower)) {
        const token = /\b(?:recently|lately|this month|last month)\b/.test(lower) ? "30d" : /\b(?:year|ages|forever)\b/.test(lower) ? "1y" : "90d";
        const word = token === "30d" ? "30 days" : token === "1y" ? "a year" : "90 days";
        return plan("stale", null, [{ query: `lastcontact<${token}`, scope: "exact" }], word, 0);
    }
    // Tell me about X
    const about = /^(?:tell me (?:more )?about|what do (?:i|you|we) know about|what do i have on|summari[sz]e|give me (?:the |a )?(?:rundown|background|summary|recap|brief|info|information|details) (?:on|about|of)|background on|remind me (?:who|about)|info on|information on|what(?:'s| is) (?:the )?(?:story|deal|news) (?:with|on|about)|how do i know|what(?:'s| is) up with|recap on|catch me up on)\s+(.+)$/.exec(lower);
    if (about) {
        const name = nameIn(q.slice(q.length - about[1].length), ABOUT_WORDS);
        if (name && name.split(/\s+/).length <= 4)
            return plan("about", name, [{ query: keywordQuery("name", name), scope: "exact" }, { query: name.toLowerCase(), scope: "broad" }]);
    }
    // Who is in group X? A known group name anywhere in the question, or whatever is left once the group words go.
    const names = (ctx.groupNames ?? []).filter(Boolean).sort((a, b) => b.length - a.length);
    const groupWords = /\b(?:groups?|members?|belongs?|belonging|put\s+(?:\w+\s+)?in)\b/.test(lower);
    const known = names.find((n) => new RegExp(`(^|[^\\p{L}\\p{N}])${n.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^\\p{L}\\p{N}]|$)`, "u").test(lower));
    if (known) {
        const rest = lower.replace(known.toLowerCase(), " ").replace(GROUP_FILLER, " ").replace(/[^\p{L}\p{N}]+/gu, " ").trim();
        if (groupWords || rest === "")
            return plan("group", known, [{ query: keywordQuery("group", known), scope: "exact" }]);
    }
    if (groupWords && !/\b(?:at|works?|employed|company)\b/.test(lower)) {
        const subject = clean(lower.replace(GROUP_FILLER, " ").replace(/\s+/g, " "));
        if (subject) {
            // Keep the user's own capitalisation of the name for the answer.
            const pos = lower.indexOf(subject);
            const shown = pos >= 0 ? q.slice(pos, pos + subject.length) : subject;
            return plan("group", shown, [{ query: keywordQuery("group", subject), scope: "exact" }]);
        }
    }
    // Who needs X? (someone else's need)
    const need = /\b(?:who(?:'s| is| are)?|anyone|anybody|people|which (?:of my )?(?:people|contacts))\s+(?:(?:is|are)\s+)?(?:(?:still\s+)?(?:needs?|needing|wants?|wanting|looking\s+for|searching\s+for|seeking|hunting\s+for|after|has\s+(?:a|an)\s+need\s+for|could\s+use|requires?|asking\s+for|in\s+the\s+market\s+for))\s+(?:help\s+with\s+|a\s+|an\s+|some\s+)?(.+)$/.exec(lower) ??
        /^(?:open\s+)?needs?\s+(?:for|about|around)\s+(.+)$/.exec(lower);
    if (need) {
        const subject = clean(q.slice(q.length - need[1].length));
        if (subject)
            return plan("need", subject, fieldCandidates("need", subject, { tokens: true, freeText: false }));
    }
    // Who can help with X? (an offer; "I need a plumber" asks the same thing)
    const offer = /\b(?:who|anyone|anybody|someone|people|which of my (?:people|contacts))\s+(?:who\s+|that\s+)?(?:(?:can|could|would|might|should)\s+(?:help\s+(?:me\s+|us\s+)?(?:with|find|do|build|fix|on)|do|build|design|write|fix|handle|take care of|advise (?:me )?on|teach)|(?:offers?|offering|provides?|providing|does|do(?!\s+(?:i|we|you)\b)|sells?|specialis[sz]es? in|knows?|(?:is\s+|are\s+)?(?:good|great) at|works? in|works? on))\s+(?:about\s+|a\s+|an\s+|some\s+|my\s+)?(.+)$/.exec(lower) ??
        /\b(?:i|we)\s+(?:need|want|am looking for|are looking for|'m looking for|could use|require)\s+(?:a\s+|an\s+|some\s+|help\s+with\s+)?(.+)$/.exec(lower) ??
        /^(?:looking for|find me|recommend|need|i'?m looking for|help with|help me (?:with|find))\s+(?:a\s+|an\s+|some\s+)?(.+)$/.exec(lower) ??
        /\b(?:should|can|could)\s+i\s+(?:hire|ask|call|talk to)\s+(?:for|about|to do)\s+(?:a\s+|an\s+|some\s+|my\s+)?(.+)$/.exec(lower) ??
        /\bknow\s+(?:a|an|any)\s+(?:good\s+|great\s+)?(.+)$/.exec(lower);
    if (offer) {
        const captured = clean(q.slice(q.length - offer[1].length));
        const loc = /^(.*?)\s+(?:in|near|around)\s+(.+)$/.exec(captured);
        const subject = loc ? clean(loc[1]) : captured;
        if (subject) {
            const candidates = fieldCandidates("service", subject, { tokens: true, freeText: true });
            if (loc)
                for (const c of candidates)
                    c.query = `${c.query} ${keywordQuery("location", clean(loc[2]))}`;
            return plan("offer", subject, candidates);
        }
    }
    // Who is Jeff? (a name, not "who is a founder" or "who is in Austin")
    const whoIs = /^who(?:'s| is)\s+(?!(?:a|an|the|my|our|your|in|at|from|on|with|near|around|based|good|great|still|here|there|new|closest|closer|close|overdue|due|\w+ing)\b)(.+)$/.exec(lower);
    if (whoIs) {
        const name = nameIn(q.slice(q.length - whoIs[1].length), ABOUT_WORDS);
        if (name && name.split(/\s+/).length <= 4)
            return plan("about", name, [{ query: keywordQuery("name", name), scope: "exact" }, { query: name.toLowerCase(), scope: "broad" }]);
    }
    // Who do I know at X? Who do I know in X?
    const forCompany = q.replace(/\b(?:works?|working|worked|employed|employees?|staff)\s+(?:at|for|with|of)\b/i, "at");
    const company = /\b(?:at|from)\s+(?:the\s+company\s+)?(.+)$/i.exec(forCompany);
    const location = /\b(?:in|near|around|based\s+in|living\s+in|lives?\s+in)\s+(.+)$/i.exec(q);
    const companyWords = /\b(?:at|works?|working|worked|employed|employees?|staff|company|from)\b/.test(lower);
    const locationWords = /\b(?:in|near|around|based|lives?|living|located)\b/.test(lower);
    if (company && companyWords && (!location || company.index <= location.index)) {
        const { head, tail } = splitTail(clean(company[1]), /^(.*?)\s+((?:in|near|around)\s+.*)$/i);
        if (head) {
            const extra = [...prefixClauses(forCompany.slice(0, company.index)), ...secondaryClauses(tail)];
            const candidates = fieldCandidates("company", head, { tokens: false, freeText: true }).map((c) => ({ ...c, query: [c.query, ...extra].join(" ") }));
            return plan("company", head, candidates);
        }
    }
    if (location && locationWords) {
        const { head, tail } = splitTail(clean(location[1]), /^(.*?)\s+((?:at|from)\s+.*)$/i);
        if (head) {
            const extra = [...prefixClauses(q.slice(0, location.index)), ...secondaryClauses(tail)];
            const candidates = fieldCandidates("location", head, { tokens: false, freeText: true }).map((c) => ({ ...c, query: [c.query, ...extra].join(" ") }));
            return plan("location", head, candidates);
        }
    }
    // Who is a founder? Which of my people are designers?
    const title = /^(?:who\s+(?:is|are|'s)\s+(?:(?:a|an|the|my)\s+)?|which\s+(?:of\s+my\s+)?(?:contacts|people)\s+(?:is|are)\s+(?:(?:a|an)\s+)?|all\s+(?:the\s+|of\s+the\s+|my\s+)?|list\s+(?:the\s+|my\s+)?|my\s+|any\s+|show\s+me\s+(?:the\s+|my\s+)?|who\s+works?\s+as\s+(?:(?:a|an)\s+)?|who\s+has\s+)(.+?)(?:\s+(?:contacts|people|i\s+know|do\s+i\s+know))?$/.exec(lower);
    if (title) {
        const pos = lower.indexOf(title[1]);
        const subject = clean(q.slice(pos, pos + title[1].length));
        if (subject && keyTokens(subject).length > 0 && !/^(?:the|my|all)$/.test(subject.toLowerCase())) {
            const { text } = rewriteSearchPhrases(subject.toLowerCase());
            if (isGrammar(text))
                return plan("search", null, [{ query: text.trim().replace(/\s+/g, " "), scope: "exact" }]);
            return plan("title", subject, fieldCandidates("title", subject, { tokens: true, freeText: true }));
        }
    }
    // A bare name: "Jeff Gubicza".
    const capitalised = q.split(" ").every((w) => /^\p{Lu}/u.test(w));
    if (/^[\p{L}][\p{L}'.-]*(?:\s+[\p{L}][\p{L}'.-]*){1,2}$/u.test(q) && keyTokens(q).length === q.split(" ").length && (capitalised || q.split(" ").length === 2)) {
        const { text } = rewriteSearchPhrases(lower);
        if (!isGrammar(text))
            return plan("about", q, [{ query: keywordQuery("name", q), scope: "exact" }, { query: lower, scope: "broad" }]);
    }
    // Whatever is left: the parser's own phrases ("closest friends", "hub users"), else free text.
    const { text } = rewriteSearchPhrases(lower);
    if (isGrammar(text))
        return plan("search", null, [{ query: text.trim().replace(/\s+/g, " "), scope: "exact" }]);
    const tokens = keyTokens(q);
    return plan("search", tokens.join(" ") || q, [{ query: tokens.length ? tokens.join(" ") : lower, scope: "broad" }]);
}
/** Grammar clauses for the tail of a question: "...who can help with design", "...in austin", "...closest". */
function secondaryClauses(tail) {
    if (!tail)
        return [];
    const out = [];
    const lower = tail.toLowerCase();
    const offer = /\b(?:can|could|who can|who could|that can)?\s*(?:help\s+(?:me\s+)?(?:with|find|do|build|fix)|offers?|provides?|does|is good at|are good at|knows?)\s+(?:a\s+|an\s+)?(.+)$/.exec(lower);
    if (offer) {
        const subject = clean(offer[1]);
        if (subject)
            out.push(keywordQuery("service", keyTokens(subject).join(" ") || subject));
        return out;
    }
    const loc = /^(?:who\s+(?:is|are|live|lives)\s+|that\s+(?:is|are|live|lives)\s+)?(?:in|near|around)\s+(.+)$/.exec(lower);
    if (loc) {
        const subject = clean(loc[1]);
        if (subject)
            out.push(keywordQuery("location", subject));
        return out;
    }
    const { text } = rewriteSearchPhrases(lower.replace(/^(?:and|who|that|which|are|is)\s+/, ""));
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length && words.every((w) => /^[\[\]()-]*(?:[a-z_]+[:<>=!]\S|has:|missing:)/.test(w) || /^[\[\]()]+$/.test(w)))
        out.push(text.trim().replace(/\s+/g, " "));
    return out;
}
// ---------------------------------------------------------------------------
// Running the plan
// ---------------------------------------------------------------------------
const PEOPLE_SHOWN = 24;
const TYPE_WORDS = { email: "an email", call: "a call", message_day: "messages", event: "a meeting", hub_message: "a Hub message", manual: "a note" };
export function ago(iso, now) {
    if (!iso)
        return "never";
    const days = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 86_400_000));
    if (days === 0)
        return "today";
    if (days === 1)
        return "yesterday";
    if (days < 30)
        return `${days} days ago`;
    const months = Math.round(days / 30);
    if (days < 365)
        return `${months} month${months === 1 ? "" : "s"} ago`;
    const years = Math.round(days / 365);
    return `${years} year${years === 1 ? "" : "s"} ago`;
}
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
/** "Thu Oct 30, 2:00 PM" in the viewer's time zone, the way the Mac's Home shows a meeting. */
export function whenText(iso, now) {
    const d = new Date(iso);
    const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const day = sameDay(d, now) ? "Today" : sameDay(d, tomorrow) ? "Tomorrow" : `${DAY_SHORT[d.getDay()]} ${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
    let h = d.getHours();
    const m = d.getMinutes();
    const suffix = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${day}, ${h}:${m.toString().padStart(2, "0")} ${suffix}`;
}
/** The days a meeting window covers, in local time: Monday to Sunday for a week, else the next seven days. */
export function windowRange(token, now) {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
    const monday = addDays(today, -((today.getDay() + 6) % 7));
    const monthStart = (offset) => new Date(now.getFullYear(), now.getMonth() + offset, 1);
    switch (token) {
        case "today":
            return { start: today, end: addDays(today, 1) };
        case "tomorrow":
            return { start: addDays(today, 1), end: addDays(today, 2) };
        case "thisweek":
            return { start: monday, end: addDays(monday, 7) };
        case "nextweek":
            return { start: addDays(monday, 7), end: addDays(monday, 14) };
        case "thismonth":
            return { start: monthStart(0), end: monthStart(1) };
        case "nextmonth":
            return { start: monthStart(1), end: monthStart(2) };
        default:
            return { start: now, end: addDays(today, 8) };
    }
}
function nameList(names, max = 3) {
    const shown = names.slice(0, max);
    if (names.length > max)
        shown.push(`and ${names.length - max} more`);
    return shown.join(", ");
}
function plural(n, one, many) {
    return `${n} ${n === 1 ? one : many}`;
}
function person(row, why) {
    return { contactId: row.contactId, displayName: row.displayName, avatarUrl: row.avatarUrl, headline: row.headline, company: row.company, why };
}
/** "Estimator, Faulkner Collision"; the company once when the headline already names it. */
function headlineAndCompany(r) {
    if (!r.company)
        return r.headline;
    if (!r.headline || r.headline.toLowerCase().includes(r.company.toLowerCase()))
        return r.headline ?? r.company;
    return `${r.headline}, ${r.company}`;
}
function byScore(rows) {
    return [...rows].sort((a, b) => b.score - a.score || a.displayName.localeCompare(b.displayName));
}
function has(text, needle) {
    return !!text && text.toLowerCase().includes(needle.toLowerCase());
}
/** The need or offer rows of these people that mention the subject, or all of them when none mention it. */
function mentioning(rows, subject, text) {
    const tokens = keyTokens(subject);
    const out = new Map();
    for (const r of rows) {
        const t = text(r).toLowerCase();
        const hit = has(t, subject) || tokens.some((k) => t.includes(k));
        if (hit && !out.has(r.contactId))
            out.set(r.contactId, r);
    }
    for (const r of rows)
        if (!out.has(r.contactId))
            out.set(r.contactId, r);
    return out;
}
/** Token overlap fallback when no matching package is injected: the share of the need's words the offer covers. */
function overlapScore(needText, offerText) {
    const need = new Set(keyTokens(needText));
    const offer = new Set(keyTokens(offerText));
    if (need.size === 0 || offer.size === 0)
        return 0;
    let inter = 0;
    for (const t of need)
        if (offer.has(t))
            inter++;
    const union = need.size + offer.size - inter;
    return 0.65 * (inter / need.size) + 0.35 * (inter / union);
}
export function personSummary(row, highlights, notes, now) {
    const first = row.firstName ?? row.displayName.split(" ")[0];
    const lastTalk = row.lastInteractionDate
        ? `You were last in touch ${ago(row.lastInteractionDate, now)}${row.lastInteractionType ? ` (${TYPE_WORDS[row.lastInteractionType] ?? row.lastInteractionType})` : ""}.`
        : `Hub has no calls, messages, emails or meetings with ${first} on record.`;
    const same = (a, b) => !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
    // Imports often put the company in the headline too; say it once.
    const headline = same(row.headline, row.company) ? null : row.headline;
    const headlineNamesCompany = !!headline && !!row.company && headline.toLowerCase().includes(row.company.toLowerCase());
    const who = [headline, row.company && !headlineNamesCompany ? `at ${row.company}` : null].filter(Boolean).join(" ") || null;
    const where = row.location ? `in ${row.location}` : null;
    const intro = [row.displayName, who ? `is ${who}` : null, where].filter(Boolean).join(" ");
    const known = new Set([row.company, row.headline, row.location].filter((v) => !!v).map((v) => v.trim().toLowerCase()));
    const facts = highlights
        // Interaction counts are in the second sentence; the notes themselves follow, so their count is noise.
        .filter((h) => h.kind !== "relationship" && (h.kind !== "notes" || notes.length === 0))
        .map((h) => h.text.trim().replace(/\.$/, ""))
        // "Currently Faulkner" repeats the company the first sentence already gave.
        .filter((t) => !known.has(t.replace(/^(currently|works? at|at)\s+/i, "").trim().toLowerCase()));
    const parts = [intro.endsWith(".") ? intro : `${intro}.`, lastTalk];
    if (row.nextEventDate)
        parts.push(`Next meeting ${whenText(row.nextEventDate, now)}.`);
    if (facts.length)
        parts.push(`${facts.slice(0, 3).join(". ")}.`);
    const noteLines = notes.slice(0, 2).map((n) => {
        const text = n.plainText.replace(/\s+/g, " ").trim();
        return `Note (${ago(n.created, now)}): ${text.length > 140 ? `${text.slice(0, 137)}...` : text}`;
    });
    return { answer: parts.join(" "), facts: [...facts.slice(3), ...noteLines] };
}
/** Runs a plan against a workspace and composes the answer from what came back. */
export async function askWorkspace(question, source, ctx = {}) {
    const plan = planQuestion(question, ctx);
    const now = source.now();
    const base = { question, intent: plan.intent, subject: plan.subject, query: "", chips: [], warnings: [], answer: "", people: [], total: 0, facts: [], broadened: false };
    if (plan.candidates.length === 0) {
        if (plan.intent === "last_talk")
            return { ...base, answer: "Who do you mean? Ask it as \"When did I last talk to <name>?\" or open Ask Hub from their page." };
        if (plan.intent === "introduce")
            return { ...base, answer: "Who do you mean? Ask it as \"Who could I introduce <name> to?\" or open Ask Hub from their page." };
        return { ...base, answer: "Ask about your people: who you know at a company or in a city, who can help with something, who needs something, who you have not talked to, who you are meeting, or what you know about someone." };
    }
    // Try each query until one returns rows; the exact one's chips explain an empty answer.
    let chosen = null;
    let firstEmpty = null;
    for (const candidate of plan.candidates) {
        const r = await source.search(candidate.query, plan.intent === "about" || plan.intent === "last_talk" || plan.intent === "introduce" ? 5 : 200);
        if (r.contacts.length > 0) {
            chosen = { candidate, contacts: r.contacts, total: r.total, parsed: r.parsed };
            break;
        }
        if (!firstEmpty)
            firstEmpty = { candidate, parsed: r.parsed };
    }
    const exact = plan.candidates[0];
    const broadened = Boolean(chosen && chosen.candidate.scope === "broad");
    const query = chosen?.candidate.query ?? exact.query;
    const parsed = chosen?.parsed ?? firstEmpty?.parsed ?? { ast: { kind: "all" }, freeText: "", chips: [], warnings: [] };
    const out = { ...base, query, chips: parsed.chips, warnings: parsed.warnings, broadened };
    const rows = chosen?.contacts ?? [];
    // The evaluator caps the rows it hands back; the count in the answer is the whole match.
    const total = Math.max(rows.length, chosen?.total ?? 0);
    const subj = plan.subject ?? "";
    switch (plan.intent) {
        case "company":
        case "location": {
            const sorted = byScore(rows);
            const at = plan.intent === "company" ? "at" : "in";
            out.people = sorted.slice(0, PEOPLE_SHOWN).map((r) => person(r, broadened ? r.headline ?? r.company ?? r.location : plan.intent === "company" ? headlineAndCompany(r) : r.location ?? r.headline));
            out.total = total;
            // "Who in Austin can help with design": the place had people, the combination did not.
            const alsoAsked = parsed.chips.slice(1);
            if (sorted.length === 0 && alsoAsked.length > 0)
                out.answer = `Nobody ${at} ${subj} also matches ${alsoAsked.join(" and ").toLowerCase()}.`;
            else if (sorted.length === 0)
                out.answer = plan.intent === "company" ? `Nobody in your people works at ${subj}.` : `Nobody in your people is in ${subj}.`;
            else if (broadened)
                out.answer = `Nobody lists ${subj} as their ${plan.intent === "company" ? "company" : "location"}, but ${plural(total, "person mentions", "people mention")} ${subj}: ${nameList(sorted.map((r) => r.displayName))}.`;
            else
                out.answer = `${plural(total, "person", "people")} ${at} ${subj}; closest first: ${nameList(sorted.map((r) => r.displayName))}.`;
            return out;
        }
        case "offer":
        case "need": {
            const sorted = byScore(rows);
            const ids = sorted.map((r) => r.contactId);
            const items = plan.intent === "offer" ? await source.offers(ids) : await source.needs(ids);
            const text = (r) => `${r.title} ${"shortDescription" in r ? r.shortDescription : r.description} ${r.keywords.join(" ")}`;
            const why = mentioning(items, subj, text);
            out.people = sorted.slice(0, PEOPLE_SHOWN).map((r) => {
                const item = why.get(r.contactId);
                return person(r, item ? `${plan.intent === "offer" ? "offers" : "needs"} ${item.title}` : broadened ? r.headline : null);
            });
            out.total = total;
            out.facts = sorted
                .slice(0, 6)
                .map((r) => why.get(r.contactId))
                .filter((i) => Boolean(i))
                .map((i) => {
                const owner = sorted.find((r) => r.contactId === i.contactId)?.displayName ?? "";
                const detail = "shortDescription" in i ? i.shortDescription : i.description;
                return `${owner}: ${i.title}${detail ? `. ${detail}` : ""}`;
            });
            if (sorted.length === 0)
                out.answer = plan.intent === "offer" ? `Nobody you know has listed ${subj} as something they offer, and nothing in your notes mentions it.` : `Nobody you know has an open need for ${subj}.`;
            else if (broadened)
                out.answer = `Nobody lists ${subj} as an offer, but ${plural(total, "person mentions", "people mention")} it in their profile, notes or needs: ${nameList(sorted.map((r) => r.displayName))}.`;
            else
                out.answer = plan.intent === "offer" ? `${plural(total, "person", "people")} can help with ${subj}: ${nameList(sorted.map((r) => r.displayName))}.` : `${plural(total, "person needs", "people need")} ${subj}: ${nameList(sorted.map((r) => r.displayName))}.`;
            return out;
        }
        case "title": {
            const sorted = byScore(rows);
            out.people = sorted.slice(0, PEOPLE_SHOWN).map((r) => person(r, r.headline ?? r.company));
            out.total = total;
            if (sorted.length === 0)
                out.answer = `No one with ${subj} in their title.`;
            else if (broadened)
                out.answer = `No one has ${subj} in their title, but ${plural(total, "person mentions", "people mention")} it: ${nameList(sorted.map((r) => r.displayName))}.`;
            else
                out.answer = `${plural(total, "person", "people")} with ${subj} in their title: ${nameList(sorted.map((r) => r.displayName))}.`;
            return out;
        }
        case "stale": {
            const sorted = byScore(rows);
            out.people = sorted.slice(0, PEOPLE_SHOWN).map((r) => person(r, `last in touch ${ago(r.lastInteractionDate, now)}${r.lastInteractionType ? ` (${TYPE_WORDS[r.lastInteractionType] ?? r.lastInteractionType})` : ""}`));
            out.total = total;
            out.answer = sorted.length === 0 ? `You have been in touch with everyone in the last ${plan.window}.` : `${plural(total, "person", "people")} you have not been in touch with for ${plan.window}; closest first: ${nameList(sorted.map((r) => r.displayName))}.`;
            return out;
        }
        case "meetings": {
            const range = windowRange(plan.windowToken, now);
            const events = (await source.events(Math.max(1, Math.ceil((range.end.getTime() - now.getTime()) / 86_400_000) + 1))).filter((e) => {
                const t = new Date(e.date).getTime();
                return t >= range.start.getTime() && t < range.end.getTime();
            });
            const byId = new Map(rows.map((r) => [r.contactId, r]));
            const seen = new Set();
            const people = [];
            const facts = [];
            for (const e of events) {
                const names = e.contactIds.map((id) => byId.get(id)).filter((r) => Boolean(r));
                if (names.length === 0)
                    continue;
                facts.push(`${whenText(e.date, now)}: ${e.title ?? "Meeting"} with ${nameList(names.map((r) => r.displayName), 4)}`);
                for (const r of names) {
                    if (seen.has(r.contactId))
                        continue;
                    seen.add(r.contactId);
                    people.push(person(r, `${whenText(e.date, now)}${e.title ? `, ${e.title}` : ""}`));
                }
            }
            // People the grammar matched but whose meeting the event list did not carry still belong in the answer.
            for (const r of [...rows].sort((a, b) => (a.nextEventDate ?? "").localeCompare(b.nextEventDate ?? ""))) {
                if (seen.has(r.contactId))
                    continue;
                seen.add(r.contactId);
                people.push(person(r, r.nextEventDate ? whenText(r.nextEventDate, now) : null));
            }
            out.people = people.slice(0, PEOPLE_SHOWN);
            out.total = people.length;
            out.facts = facts.slice(0, 12);
            const w = plan.window ?? "this week";
            out.answer = people.length === 0 ? `No meetings with your people ${w}.` : `${plural(facts.length || people.length, "meeting", "meetings")} ${w} with ${plural(people.length, "person", "people")}: ${nameList(people.map((p) => p.displayName), 4)}.`;
            return out;
        }
        case "birthdays": {
            const dated = rows
                .filter((r) => r.birthday)
                .map((r) => {
                const b = r.birthday;
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const thisYear = new Date(now.getFullYear(), b.month - 1, b.day);
                const passed = Math.round((today.getTime() - thisYear.getTime()) / 86_400_000);
                // A birthday earlier in the asked window reads as "4 days ago", not as next year's.
                const next = passed > 0 && passed <= 45 ? thisYear : thisYear < today ? new Date(now.getFullYear() + 1, b.month - 1, b.day) : thisYear;
                const days = Math.round((next.getTime() - today.getTime()) / 86_400_000);
                return { r, next, days };
            })
                .sort((a, b) => a.next.getTime() - b.next.getTime());
            const when = (days) => (days === 0 ? "today" : days === 1 ? "tomorrow" : days === -1 ? "yesterday" : days < 0 ? `${-days} days ago` : `in ${days} days`);
            out.people = dated.slice(0, PEOPLE_SHOWN).map(({ r, next, days }) => person(r, `${MONTH_SHORT[next.getMonth()]} ${next.getDate()} (${when(days)})`));
            out.total = dated.length;
            const w = plan.window ?? "coming up";
            out.answer = dated.length === 0 ? `No birthdays ${w}.` : `${plural(dated.length, "birthday", "birthdays")} ${w}: ${nameList(dated.map(({ r, next }) => `${r.displayName} (${MONTH_SHORT[next.getMonth()]} ${next.getDate()})`), 4)}.`;
            return out;
        }
        case "group": {
            const sorted = byScore(rows);
            out.people = sorted.slice(0, PEOPLE_SHOWN).map((r) => person(r, r.headline ?? r.company));
            out.total = total;
            out.answer = sorted.length === 0 ? `No group called ${subj}, or nobody is in it.` : `${plural(total, "person", "people")} in ${subj}: ${nameList(sorted.map((r) => r.displayName))}.`;
            return out;
        }
        case "about":
        case "last_talk": {
            const row = pickPerson(rows, subj, ctx);
            if (!row) {
                out.answer = `No one called ${subj} in your people.`;
                return out;
            }
            out.query = keywordQuery("name", row.displayName);
            out.chips = [`Name is ${row.displayName}`];
            out.people = [person(row, row.headline ?? row.company)];
            out.total = 1;
            if (plan.intent === "last_talk") {
                out.answer = row.lastInteractionDate
                    ? `You were last in touch with ${row.displayName} ${ago(row.lastInteractionDate, now)}${row.lastInteractionType ? ` (${TYPE_WORDS[row.lastInteractionType] ?? row.lastInteractionType})` : ""}.`
                    : `Hub has no calls, messages, emails or meetings with ${row.displayName} on record.`;
                if (row.nextEventDate)
                    out.facts = [`Next meeting ${whenText(row.nextEventDate, now)}.`];
                return out;
            }
            const [highlights, notes] = await Promise.all([source.highlights(row.contactId).catch(() => []), source.notes(row.contactId).catch(() => [])]);
            const summary = personSummary(row, highlights, notes, now);
            out.answer = summary.answer;
            out.facts = summary.facts;
            if (rows.length > 1)
                out.facts.push(`Also matched: ${nameList(rows.filter((r) => r.contactId !== row.contactId).map((r) => r.displayName), 4)}.`);
            return out;
        }
        case "introduce": {
            const row = pickPerson(rows, subj, ctx);
            if (!row) {
                out.answer = `No one called ${subj} in your people.`;
                return out;
            }
            return introduce(row, source, out, now);
        }
        case "search":
        default: {
            const sorted = byScore(rows);
            out.people = sorted.slice(0, PEOPLE_SHOWN).map((r) => person(r, r.headline ?? r.company ?? r.location));
            out.total = total;
            out.answer = sorted.length === 0 ? `No one matches ${subj || query}.` : `${plural(total, "person matches", "people match")} ${subj || query}: ${nameList(sorted.map((r) => r.displayName))}.`;
            return out;
        }
    }
}
/** The one person a name question meant: the panel's person when they match, else the closest name match. */
function pickPerson(rows, subject, ctx) {
    if (rows.length === 0)
        return null;
    const fromContext = ctx.person ? rows.find((r) => r.contactId === ctx.person.contactId) : null;
    if (fromContext)
        return fromContext;
    const lower = subject.toLowerCase();
    const exact = rows.find((r) => r.displayName.toLowerCase() === lower);
    if (exact)
        return exact;
    return byScore(rows)[0];
}
/** Who this person could be introduced to: their offers against open needs, their needs against offers. */
async function introduce(row, source, out, now) {
    const score = source.scoreIntro ?? overlapScore;
    const [mine, myNeeds] = await Promise.all([source.offers([row.contactId]), source.needs([row.contactId])]);
    const first = row.firstName ?? row.displayName.split(" ")[0];
    if (mine.length === 0 && myNeeds.length === 0) {
        out.query = keywordQuery("name", row.displayName);
        out.chips = [`Name is ${row.displayName}`];
        out.people = [person(row, row.headline ?? row.company)];
        out.total = 1;
        out.answer = `${row.displayName} has no offers or needs on record, so there is nothing to match yet. Add what ${first} offers or needs on their page.`;
        return out;
    }
    // The grammar that finds the other side: open needs mentioning the offer's words, offers mentioning the need's.
    const clauses = [];
    for (const o of mine)
        for (const t of keyTokens(`${o.title} ${o.keywords.join(" ")}`).slice(0, 4))
            clauses.push(keywordQuery("need", t));
    for (const n of myNeeds)
        for (const t of keyTokens(`${n.title} ${n.keywords.join(" ")}`).slice(0, 4))
            clauses.push(keywordQuery("service", t));
    const unique = [...new Set(clauses)];
    const query = unique.length === 1 ? unique[0] : `[${unique.join(" ")}]`;
    const found = await source.search(query, 200);
    out.query = query;
    out.chips = found.parsed.chips;
    out.warnings = found.parsed.warnings;
    let others = found.contacts.filter((r) => r.contactId !== row.contactId);
    let ids = others.map((r) => r.contactId);
    let [theirNeeds, theirOffers] = await Promise.all([mine.length ? source.needs(ids) : Promise.resolve([]), myNeeds.length ? source.offers(ids) : Promise.resolve([])]);
    // The words did not meet ("wrecked cars" against "collision repair"): score every need and offer instead,
    // which is what the matching package does when it finds introductions, and say the search went wider.
    if (theirNeeds.length === 0 && theirOffers.length === 0) {
        const [allNeeds, allOffers] = await Promise.all([mine.length ? source.needs() : Promise.resolve([]), myNeeds.length ? source.offers() : Promise.resolve([])]);
        theirNeeds = allNeeds.filter((n) => n.contactId !== row.contactId);
        theirOffers = allOffers.filter((o) => o.contactId !== row.contactId);
        ids = [...new Set([...theirNeeds, ...theirOffers].map((r) => r.contactId))];
        others = ids.length ? await source.rows(ids) : [];
        out.broadened = ids.length > 0;
    }
    const pairs = [];
    const byId = new Map(others.map((r) => [r.contactId, r]));
    for (const o of mine) {
        const offerText = `${o.title} ${o.shortDescription} ${o.keywords.join(" ")}`;
        for (const n of theirNeeds) {
            const other = byId.get(n.contactId);
            if (!other)
                continue;
            const s = score(`${n.title} ${n.description} ${n.keywords.join(" ")}`, offerText);
            pairs.push({ other, score: s, why: `needs ${n.title}`, fact: `${other.displayName} needs ${n.title}; ${first} offers ${o.title} (${label(s)})` });
        }
    }
    for (const n of myNeeds) {
        const needText = `${n.title} ${n.description} ${n.keywords.join(" ")}`;
        for (const o of theirOffers) {
            const other = byId.get(o.contactId);
            if (!other)
                continue;
            const s = score(needText, `${o.title} ${o.shortDescription} ${o.keywords.join(" ")}`);
            pairs.push({ other, score: s, why: `offers ${o.title}`, fact: `${other.displayName} offers ${o.title}; ${first} needs ${n.title} (${label(s)})` });
        }
    }
    // The grammar casts a wide net on single words; the score keeps only pairs that genuinely overlap.
    pairs.sort((a, b) => b.score - a.score || a.other.displayName.localeCompare(b.other.displayName));
    const seen = new Set();
    const best = pairs.filter((p) => p.score >= INTRO_MIN_SCORE && (seen.has(p.other.contactId) ? false : (seen.add(p.other.contactId), true)));
    out.people = best.slice(0, PEOPLE_SHOWN).map((p) => person(p.other, p.why));
    out.total = best.length;
    out.facts = best.slice(0, 8).map((p) => p.fact);
    const offers = mine.map((o) => o.title);
    const needs = myNeeds.map((n) => n.title);
    const has = [offers.length ? `offers ${nameList(offers, 2)}` : null, needs.length ? `needs ${nameList(needs, 2)}` : null].filter(Boolean).join(" and ");
    out.answer = best.length === 0 ? `${row.displayName} ${has}; nobody in your people has a matching need or offer right now.` : `${plural(best.length, "introduction", "introductions")} for ${row.displayName}, who ${has}: ${nameList(best.map((p) => p.other.displayName))}.`;
    return out;
}
/** MATCHING.minTextScore: text similarity a pair must reach on its own. */
const INTRO_MIN_SCORE = 0.2;
function label(score) {
    return score >= 0.6 ? "strong match" : score >= 0.35 ? "good match" : "possible match";
}
/** The first title short enough to read as a question, lower-cased except for acronyms. */
function shortTitle(titles) {
    for (const t of titles) {
        const words = t.trim().split(/\s+/).filter(Boolean);
        if (words.length >= 1 && words.length <= 4)
            return words.map((w) => (/^[A-Z0-9&]{2,}$/.test(w) ? w : w.toLowerCase())).join(" ");
    }
    return null;
}
function article(phrase) {
    const last = phrase.split(" ").pop() ?? "";
    if (/s$/i.test(last))
        return "";
    return /^[aeiou]/i.test(phrase) ? "an " : "a ";
}
/** Questions worth asking of this workspace, drawn from its own companies, places, needs, offers and groups. */
export function askStarters(facets) {
    const out = [];
    if (facets.companies[0])
        out.push(`Who do I know at ${facets.companies[0]}?`);
    if (facets.locations[0])
        out.push(`Who do I know in ${facets.locations[0]}?`);
    const offer = shortTitle(facets.offers);
    if (offer)
        out.push(`Who can help with ${offer}?`);
    const need = shortTitle(facets.needs);
    if (need)
        out.push(`Who needs ${article(need)}${need}?`);
    out.push("Who haven't I talked to in a while?", "Who am I meeting this week?", "Birthdays this month");
    if (facets.groups[0])
        out.push(`Who is in ${facets.groups[0]}?`);
    return out;
}
/** The questions offered when the panel is opened about a person. */
export function personStarters(row) {
    const first = row.firstName ?? row.displayName.split(" ")[0];
    const out = [`Tell me about ${row.displayName}`, "When did we last talk?"];
    if (row.company)
        out.push(`Who else do I know at ${row.company}?`);
    if (row.location)
        out.push(`Who do I know in ${row.location}?`);
    out.push(`Who could I introduce ${first} to?`);
    return out;
}
/** Everything Ask Hub reads, through the engine RPCs the app already has; `scoreIntro` plugs in the matching package. */
export function askSourceFromEngine(invoke, opts = {}) {
    const filter = (rows, ids) => (ids ? rows.filter((r) => ids.includes(r.contactId)) : rows);
    return {
        now: opts.now ?? (() => new Date()),
        search: async (query, limit) => {
            const r = await invoke("search", { query, limit, sort: "score", direction: "desc" });
            return { contacts: r.contacts, total: r.total, parsed: r.parsed };
        },
        needs: async (ids) => filter(await invoke("listNeeds", {}), ids),
        offers: async (ids) => filter(await invoke("listServices", {}), ids),
        events: async (days) => (await invoke("getUpcomingEvents", { days })).map((e) => ({ date: e.event?.startDatetime ?? e.interaction.date, title: e.event?.title ?? null, contactIds: e.contacts.map((c) => c.contactId) })),
        highlights: (contactId) => invoke("getContactHighlights", { contactId }),
        notes: async (contactId) => (await invoke("getContactNotes", { contactId })).map((n) => ({ plainText: n.plainText, created: n.created })),
        rows: (contactIds) => invoke("getContactRows", { contactIds }),
        scoreIntro: opts.scoreIntro,
    };
}
//# sourceMappingURL=ask.js.map